import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import {
  DollarSign, Users, CheckCircle, XCircle, TrendingUp, Calculator, FileText,
  Eye, Plus, Filter, Download, Search, ArrowRight, ArrowDown, ArrowLeft,
  Zap, Trash2, Loader2, Calendar, Upload, AlertCircle, Settings, Check,
  RefreshCw, Car, LogOut, Lock, Mail, Send, UserCheck, ChevronLeft, Printer, FilePlus2, Pencil
} from "lucide-react";
import { API, axios, formatIcelandicNumber, calculatePenaltyInterest, getLoanTypeLabel } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { calculateLoanAhk } from "@/utils/ahk";
import { useAuth } from "@/context/AuthContext";
import { MiniSpreadsheet } from "@/components/MiniSpreadsheet";
import { EmailPreviewDialog } from "@/components/EmailPreviewDialog";
import {
  SkuldabrefFormDialog,
  VedskuldabrefFormDialog,
  KaupleiguFormDialog,
  DokobitDialog,
  SkuldbreytingDialog,
  PaymentDialog,
  PdfPreviewDialog,
} from "@/components/applications";
import { NewAfsolWizard } from "@/components/afsol/NewAfsolWizard";

// Convert date string (DD.MM.YYYY or DD/M/YYYY) to YYYY-MM-DD for date inputs
const toISODate = (d) => {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const parts = d.split(/[./]/);
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  return d;
};

// Applications Component
export const Applications = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchKennitala, setSearchKennitala] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedLoanType, setSelectedLoanType] = useState(null);
  const [pdfApp, setPdfApp] = useState(null);
  const [pdfType, setPdfType] = useState(null); // 'skuldabref', 'vedskuldabref', 'kaupleigusamningur'
  const [isElectronicSignature, setIsElectronicSignature] = useState(false);
  const [settings, setSettings] = useState({ reibor: 6.16, alag: 5.04, vextir: 17.7, greidslus: 250, styrivextir: 7.25 });
  const [showSkuldabrefForm, setShowSkuldabrefForm] = useState(false);
  const [showVedskuldabrefForm, setShowVedskuldabrefForm] = useState(false);
  const [showKaupleiguForm, setShowKaupleiguForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingAppId, setEditingAppId] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentApp, setPaymentApp] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lastInvoiceSentMonth, setLastInvoiceSentMonth] = useState(null); // Track when invoices were last sent
  const [showAfsolWizard, setShowAfsolWizard] = useState(false);
  const [afsolWizardLoanData, setAfsolWizardLoanData] = useState(null);
  
  // API URL
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
  
  // Dokobit signing state
  const [showDokobitDialog, setShowDokobitDialog] = useState(false);
  const [dokobitApp, setDokobitApp] = useState(null);
  const [dokobitSigners, setDokobitSigners] = useState([]);
  const [dokobitLoading, setDokobitLoading] = useState(false);
  const [signingUrls, setSigningUrls] = useState(null);
  const [signingStatus, setSigningStatus] = useState(null);
  
  // Mini spreadsheet toggle state (data is managed in MiniSpreadsheet component)
  const [showMiniSpreadsheet, setShowMiniSpreadsheet] = useState(false);
  
  // Email preview dialog state
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState({
    to_email: '',
    recipient_name: '',
    loan_type: '',
    loan_amount: 0,
    lantokugjald: 0,
    total_loan: 0,
    interest_rate: 0,
    months: 0,
    monthly_payment: 0,
    greidslus: 0
  });
  
  // Calculate default first payment date (1st of next month)
  const getDefaultFirstPaymentDate = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return nextMonth.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const [skuldabrefFormData, setSkuldabrefFormData] = useState({
    kennitala: "",
    name: "",
    address: "",
    postalCode: "",
    city: "",
    phone: "",
    email: "",
    amount: "",
    lantokugjald: "",
    greidslus: "250",
    months: "",
    vextir: settings?.vextir?.toString() || "",
    firstPaymentDate: getDefaultFirstPaymentDate(),
    vehicleType: "",
    registrationNumber: "",
    radstofun: false,
    radstofunKennitala: "",
    radstofunName: "",
    radstofunBanki: "",
    radstofunHofudbokl: "",
    radstofunReikningur: ""
  });
  const [vedskuldabrefFormData, setVedskuldabrefFormData] = useState({
    kennitala: "",
    name: "",
    address: "",
    postalCode: "",
    city: "",
    phone: "",
    email: "",
    purchasePrice: "",
    deposit: "",
    amount: "",
    lantokugjald: "",
    greidslus: "250",
    months: "",
    vextir: settings?.vextir?.toString() || "",
    registrationNumber: "",
    vehicleType: "",
    firstRegistrationDate: "",
    firstPaymentDate: getDefaultFirstPaymentDate(),
    radstofun: false,
    radstofunKennitala: "",
    radstofunName: "",
    radstofunBanki: "",
    radstofunHofudbokl: "",
    radstofunReikningur: ""
  });
  const [kaupleiguFormData, setKaupleiguFormData] = useState({
    kennitala: "",
    name: "",
    address: "",
    postalCode: "",
    city: "",
    phone: "",
    email: "",
    vehicleType: "",
    registrationNumber: "",
    firstRegistrationDate: "",
    purchasePrice: "",
    deposit: "",
    lantokugjald: "",
    greidslus: "250",
    months: "",
    vextir: settings?.vextir?.toString() || "",
    firstPaymentDate: getDefaultFirstPaymentDate(),
    radstofun: false,
    radstofunKennitala: "",
    radstofunName: "",
    radstofunBanki: "",
    radstofunHofudbokl: "",
    radstofunReikningur: ""
  });
  
  // Skuldbreyting state
  const [showSkuldbreytingDialog, setShowSkuldbreytingDialog] = useState(false);
  const [skuldbreytingLoanNumber, setSkuldbreytingLoanNumber] = useState("");
  const [skuldbreytingLoan, setSkuldbreytingLoan] = useState(null);
  const [skuldbreytingSelectedLoans, setSkuldbreytingSelectedLoans] = useState([]); // Array of selected loans
  const [skuldbreytingStep, setSkuldbreytingStep] = useState(1); // 1: loan lookup, 2: choose type, 3: form
  const [skuldbreytingNewLoanType, setSkuldbreytingNewLoanType] = useState(null);
  const [skuldbreytingOldLoanId, setSkuldbreytingOldLoanId] = useState(null); // Track old loan for conversion (legacy single loan support)
  const [skuldbreytingOldLoanIds, setSkuldbreytingOldLoanIds] = useState([]); // Track multiple old loans for conversion
  
  const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false);
  const [vehicleLookupFailed, setVehicleLookupFailed] = useState(false);
  
  // Pre-fill vextir from settings when settings load (new forms only)
  // If there's a pending rate change, new loans use the new rate immediately
  useEffect(() => {
    if (settings && !editMode) {
      const prc = settings.pending_rate_change;
      const effectiveRate = (prc && prc.status === 'pending') 
        ? prc.new_rate.toString() 
        : (settings.vextir?.toString() || "");
      if (effectiveRate) {
        setSkuldabrefFormData(prev => ({ ...prev, vextir: effectiveRate }));
        setVedskuldabrefFormData(prev => ({ ...prev, vextir: effectiveRate }));
        setKaupleiguFormData(prev => ({ ...prev, vextir: effectiveRate }));
      }
    }
  }, [settings, editMode]);

  const location = useLocation();
  const navigate = useNavigate();

  // Handle URL query parameters for status filter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status === 'pending') {
      setStatusFilter('pending');
    } else if (status === 'approved' || status === 'disbursed') {
      setStatusFilter('virk');
    } else if (status === 'closed') {
      setStatusFilter('closed');
    }
  }, [location.search]);

  const fetchApplications = async () => {
    try {
      let applications = [];
      
      if (statusFilter === "all") {
        // Fetch all applications
        const response = await axios.get(`${API}/loans`);
        applications = response.data;
      } else if (statusFilter === "virk") {
        // Fetch all and filter for approved or disbursed
        const response = await axios.get(`${API}/loans`);
        applications = response.data.filter(app => 
          app.status === 'approved' || app.status === 'disbursed'
        );
      } else {
        // Fetch with specific status filter (pending, closed, etc.)
        const response = await axios.get(`${API}/loans?status=${statusFilter}`);
        applications = response.data;
      }
      
      setApplications(applications);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Ekki tókst að hlaða umsóknum");
    } finally {
      setLoading(false);
    }
  };

  const handleSendaAllt = async () => {
    // Get next month and year
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const nextMonthKey = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`;
    
    // Calculate number of invoices to send
    const nextMonthNumber = nextMonth.getMonth() + 1;
    const nextYear = nextMonth.getFullYear();
    
    let invoiceCount = 0;
    applications.forEach(app => {
      if (app.status === 'disbursed' && app.payment_schedule) {
        app.payment_schedule.forEach((payment, index) => {
          if (payment.date) {
            const [day, month, year] = payment.date.split('.');
            if (parseInt(month) === nextMonthNumber && parseInt(year) === nextYear) {
              const monthKey = (index + 1).toString();
              const status = app.scheduled_payments_status?.[monthKey] || 'pending';
              if (status !== 'paid' && status !== 'invoice_sent') {
                invoiceCount++;
              }
            }
          }
        });
      }
    });
    
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Staðfesta sendingu allra reikninga fyrir næsta mánuð:\n\n` +
      `Mánuður: ${nextMonth.toLocaleDateString('is-IS', { year: 'numeric', month: 'long' })}\n` +
      `Fjöldi reikninga: ${invoiceCount}\n\n` +
      `Viltu halda áfram?`
    );
    
    if (!confirmed) {
      return;
    }
    
    // Send all invoices
    try {
      toast.loading("Sendi alla reikninga...", { id: 'send-all' });
      
      const response = await axios.post(`${API}/admin/trigger-auto-send`);
      
      if (response.data.success) {
        const { invoices_sent, already_sent, errors } = response.data;
        
        if (invoices_sent > 0) {
          toast.success(
            `✓ ${invoices_sent} reikningar sendir fyrir ${nextMonth.toLocaleDateString('is-IS', { month: 'long' })}!${already_sent > 0 ? ` (${already_sent} þegar sendir)` : ''}`,
            { id: 'send-all', duration: 5000 }
          );
          
          // Save the month when invoices were sent
          localStorage.setItem('lastInvoiceSentMonth', nextMonthKey);
          setLastInvoiceSentMonth(nextMonthKey);
        } else if (already_sent > 0) {
          toast.success(
            `Allir ${already_sent} reikningar eru þegar sendir!`,
            { id: 'send-all', duration: 5000 }
          );
          // Also mark as sent to hide the button
          localStorage.setItem('lastInvoiceSentMonth', nextMonthKey);
          setLastInvoiceSentMonth(nextMonthKey);
        } else {
          toast.error("Engir reikningar fundnir til að senda", { id: 'send-all' });
        }
        
        if (errors > 0) {
          toast.error(`${errors} villur komu upp við sendingu`, { duration: 5000 });
        }
        
        // Refresh applications to show updated statuses
        fetchApplications();
      } else {
        toast.error("Villa við sendingu reikninga", { id: 'send-all' });
      }
    } catch (error) {
      console.error("Error sending all invoices:", error);
      toast.error(
        error.response?.data?.detail || "Villa við sendingu reikninga",
        { id: 'send-all' }
      );
    }
  };
  
  // Check if "Senda allt" button should be visible
  const shouldShowSendaAllt = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const nextMonthKey = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`;
    const nextMonthNumber = nextMonth.getMonth() + 1;
    const nextYear = nextMonth.getFullYear();
    
    // Count how many pending invoices exist for next month
    let pendingCount = 0;
    
    applications.forEach(app => {
      if (app.status === 'disbursed' && app.payment_schedule) {
        app.payment_schedule.forEach((payment, index) => {
          if (payment.date) {
            const [day, month, year] = payment.date.split('.');
            if (parseInt(month) === nextMonthNumber && parseInt(year) === nextYear) {
              const monthKey = (index + 1).toString();
              const status = app.scheduled_payments_status?.[monthKey] || 'pending';
              // Count if payment is pending (not paid, not sent)
              if (status === 'pending') {
                pendingCount++;
              }
            }
          }
        });
      }
    });
    
    // Show button only if:
    // 1. We haven't sent invoices for next month yet AND
    // 2. There are actually pending invoices to send
    return lastInvoiceSentMonth !== nextMonthKey && pendingCount > 0;
  };


  useEffect(() => {
    fetchApplications();
    fetchSettings();
    
    // Load last invoice sent month from localStorage
    const savedMonth = localStorage.getItem('lastInvoiceSentMonth');
    if (savedMonth) {
      setLastInvoiceSentMonth(savedMonth);
    }
  }, [statusFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "new") {
      setShowNewForm(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search]);


  // Handle Skuldbreyting step 3 - Open form with pre-filled data
  useEffect(() => {
    const prepareSkuldbreytingForm = async () => {
      // Check if we're at step 3 and have either single loan or multiple loans selected
      const hasLoans = skuldbreytingLoan || skuldbreytingSelectedLoans.length > 0;
      
      if (skuldbreytingStep === 3 && hasLoans && skuldbreytingNewLoanType) {
        // Set old loan IDs for tracking (supports both single and multiple)
        if (skuldbreytingSelectedLoans.length > 0) {
          setSkuldbreytingOldLoanIds(skuldbreytingSelectedLoans.map(l => l.id));
        } else if (skuldbreytingLoan) {
          setSkuldbreytingOldLoanId(skuldbreytingLoan.id);
          setSkuldbreytingOldLoanIds([skuldbreytingLoan.id]);
        }
        
        // Fetch current settings
        let currentInterestRate = skuldbreytingLoan?.interest_rate || 10.67;
        try {
          const settingsResponse = await axios.get(`${API}/settings`);
          if (settingsResponse.data) {
            // Use pending new rate if exists, otherwise current Vextir
            const prc = settingsResponse.data.pending_rate_change;
            currentInterestRate = (prc && prc.status === 'pending') ? prc.new_rate : (settingsResponse.data.vextir || 17.7);
          }
        } catch (error) {
          console.error("Error fetching settings:", error);
          // Fall back to old loan's interest rate if settings fetch fails
        }
        
        // Use the remaining_balance from database for all loans
        let actualRemainingBalance = 0;
        
        // Determine which loans to process:
        // 1. If loans are added to the selected array, use those
        // 2. Otherwise, if a single loan is typed (not yet added), use that
        const loansToProcess = skuldbreytingSelectedLoans.length > 0 
          ? skuldbreytingSelectedLoans 
          : (skuldbreytingLoan ? [skuldbreytingLoan] : []);
        
        loansToProcess.forEach(loan => {
          // Use remaining_balance if available, otherwise fallback to loan_amount
          actualRemainingBalance += loan.remaining_balance || loan.loan_amount;
        });
        
        // Use first selected loan for borrower info (all should have same borrower)
        const primaryLoan = loansToProcess[0];
        
        // Pre-fill borrower data with calculated remaining balance and current interest rate
        const borrowerData = {
          kennitala: primaryLoan.borrower_info.ssn || primaryLoan.borrower_info.kennitala || "",
          name: primaryLoan.borrower_info.full_name,
          address: primaryLoan.borrower_info.address,
          postalCode: primaryLoan.borrower_info.zip_code || primaryLoan.borrower_info.postal_code || "",
          city: primaryLoan.borrower_info.city || "",
          phone: primaryLoan.borrower_info.phone,
          email: primaryLoan.borrower_info.email,
          amount: Math.round(actualRemainingBalance).toString(),
          lantokugjald: "",
          greidslus: "250",
          months: "",
          vextir: currentInterestRate.toString(),
          radstofun: false,
          radstofunKennitala: "",
          radstofunName: "",
          radstofunBanki: "",
          radstofunHofudbokl: "",
          radstofunReikningur: ""
        };
      
      // Close Skuldbreyting dialog
      setShowSkuldbreytingDialog(false);
      
      // Open appropriate form based on loan type
      if (skuldbreytingNewLoanType === 'skuldabref') {
        setSkuldabrefFormData(borrowerData);
        setShowSkuldabrefForm(true);
      } else if (skuldbreytingNewLoanType === 'vedskuldabref') {
        setVedskuldabrefFormData({
          ...borrowerData,
          registrationNumber: "",
          vehicleType: "",
          firstRegistrationDate: ""
        });
        setShowVedskuldabrefForm(true);
      } else if (skuldbreytingNewLoanType === 'kaupleigusamning') {
        setKaupleiguFormData({
          ...borrowerData,
          vehicleType: "",
          registrationNumber: "",
          firstRegistrationDate: "",
          purchasePrice: borrowerData.amount, // Pre-fill with remaining balance
          deposit: ""
        });
        setShowKaupleiguForm(true);
      }
        
        // Reset Skuldbreyting state (but keep old loan ID)
        setTimeout(() => {
          setSkuldbreytingLoanNumber("Nr");
          setSkuldbreytingLoan(null);
          setSkuldbreytingStep(1);
          setSkuldbreytingNewLoanType(null);
        }, 100);
      }
    };
    
    prepareSkuldbreytingForm();
  }, [skuldbreytingStep, skuldbreytingLoan, skuldbreytingSelectedLoans, skuldbreytingNewLoanType]);


  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const lookupVehicle = async (fastanumer) => {
    if (!fastanumer || fastanumer.length < 3) return;
    setVehicleLookupLoading(true);
    setVehicleLookupFailed(false);
    try {
      const response = await axios.post(`${API}/vehicle-lookup`, { fastanumer });
      setVehicleLookupLoading(false);
      if (response.data) {
        setKaupleiguFormData(prev => ({
          ...prev,
          vehicleType: response.data.tegund || '',
          firstRegistrationDate: toISODate(response.data.fyrsta_skraning) || ''
        }));
        toast.success("Ökutækjaupplýsingar fundust!");
      }
    } catch (error) {
      setVehicleLookupLoading(false);
      setVehicleLookupFailed(true);
      // Don't clear fields — let user fill manually
      toast.info("Uppfletting mistókst — fylltu inn handvirkt", { duration: 4000 });
    }
  };

  const lookupVehicleForVedskuldabref = async (fastanumer) => {
    if (!fastanumer || fastanumer.length < 3) return;
    setVehicleLookupLoading(true);
    setVehicleLookupFailed(false);
    try {
      const response = await axios.post(`${API}/vehicle-lookup`, { fastanumer });
      setVehicleLookupLoading(false);
      if (response.data) {
        setVedskuldabrefFormData(prev => ({
          ...prev,
          vehicleType: response.data.tegund || '',
          firstRegistrationDate: toISODate(response.data.fyrsta_skraning) || ''
        }));
        toast.success("Ökutækjaupplýsingar fundust!");
      }
    } catch (error) {
      setVehicleLookupLoading(false);
      setVehicleLookupFailed(true);
      toast.info("Uppfletting mistókst — fylltu inn handvirkt", { duration: 4000 });
    }
  };

  const getStatusBadge = (status, loan = null) => {
    // Check if this is a Skuldbreyting loan
    if (loan && loan.isSkuldbreyting) {
      return (
        <Badge className="bg-red-100 text-red-800 px-2 py-1 text-xs font-bold">
          Skuldbreyting
        </Badge>
      );
    }
    
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", label: "Í bið" },
      under_review: { color: "bg-blue-100 text-blue-800", label: "Í skoðun" },
      approved: { color: "bg-green-100 text-green-800", label: "Virk" },
      rejected: { color: "bg-red-100 text-red-800", label: "Hafnað" },
      disbursed: { color: "bg-green-100 text-green-800", label: "Virk" },
      closed: { color: "bg-gray-100 text-gray-800", label: "Lokið" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={`${config.color} px-2 py-1 text-xs font-medium`}>
        {config.label}
      </Badge>
    );
  };

  const updateApplicationStatus = async (appId, status, notes = "", interestRate = null) => {
    try {
      const updateData = { status, notes };
      if (interestRate) updateData.interest_rate = parseFloat(interestRate);
      
      await axios.patch(`${API}/loans/${appId}`, updateData);
      fetchApplications();
      setSelectedApp(null);
      toast.success(`Umsókn ${status === 'approved' ? 'samþykkt' : 'hafnað'} með góðum árangri`);
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error("Ekki tókst að uppfæra umsókn");
    }
  };

  const deleteApplication = async (appId, appName) => {
    if (!window.confirm(`Ertu viss um að þú viljir eyða umsókn fyrir ${appName}?`)) {
      return;
    }

    try {
      console.log("Deleting application:", appId);
      const response = await axios.delete(`${API}/loans/${appId}`);
      console.log("Delete response:", response.data);
      
      toast.success("Umsókn eytt");
      
      // Wait a moment then refresh
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchApplications();
      console.log("Applications refreshed after delete");
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error("Ekki tókst að eyða umsókn");
    }
  };

  const handleEditApplication = (app) => {
    // If it's a legacy loan, keep the modal open and show a helpful message
    if (app.is_legacy) {
      toast.info("Þú getur breytt lánstíma og hlaðið upp nýju PDF í flipanum hér að neðan");
      // Don't close the modal for legacy loans - let them edit in place
      return;
    }
    
    // Close the view modal for regular loans
    setSelectedApp(null);
    
    // Set edit mode
    setEditMode(true);
    setEditingAppId(app.id);
    
    // Determine which form to open based on loan type
    if (app.loan_type === 'skuldabref') {
      // Pre-fill Skuldabréf form
      setSkuldabrefFormData({
        kennitala: app.borrower_info.ssn || '',
        name: app.borrower_info.full_name || '',
        address: app.borrower_info.address || '',
        postalCode: app.borrower_info.zip_code || '',
        city: app.borrower_info.city || '',
        phone: app.borrower_info.phone?.replace('+354', '') || '',
        email: app.borrower_info.email || '',
        amount: app.loan_amount?.toString() || '',
        lantokugjald: (app.lantokugjald || app.origination_fee || '')?.toString(),
        greidslus: app.greidslus?.toString() || '250',
        months: app.requested_term_months?.toString() || '',
        vextir: app.interest_rate?.toString() || settings?.vextir?.toString() || '',
        firstPaymentDate: app.first_payment_date || '',
        vehicleType: app.formData?.vehicleType || app.vehicle_type || '',
        registrationNumber: app.formData?.registrationNumber || app.registration_number || '',
        firstRegistrationDate: toISODate(app.first_registration_date || app.formData?.firstRegistrationDate) || ''
      });
      setShowSkuldabrefForm(true);
    } else if (app.loan_type === 'vedskuldabref') {
      // Pre-fill Veðskuldabréf form
      setVedskuldabrefFormData({
        kennitala: app.borrower_info.ssn || '',
        name: app.borrower_info.full_name || '',
        address: app.borrower_info.address || '',
        postalCode: app.borrower_info.zip_code || '',
        city: app.borrower_info.city || '',
        phone: app.borrower_info.phone?.replace('+354', '') || '',
        email: app.borrower_info.email || '',
        amount: app.loan_amount?.toString() || '',
        lantokugjald: (app.lantokugjald || app.origination_fee || '')?.toString(),
        greidslus: app.greidslus?.toString() || '250',
        months: app.requested_term_months?.toString() || '',
        vextir: app.interest_rate?.toString() || settings?.vextir?.toString() || '',
        purchasePrice: app.purchase_price?.toString() || '',
        deposit: app.deposit?.toString() || '0',
        registrationNumber: app.registration_number || app.formData?.registrationNumber || '',
        vehicleType: app.vehicle_type || app.formData?.vehicleType || '',
        firstRegistrationDate: toISODate(app.first_registration_date) || ''
      });
      setShowVedskuldabrefForm(true);
    } else if (app.loan_type === 'kaupleigusamning') {
      // Pre-fill Kaupleigusamningur form
      setKaupleiguFormData({
        kennitala: app.borrower_info.ssn || '',
        name: app.borrower_info.full_name || '',
        address: app.borrower_info.address || '',
        postalCode: app.borrower_info.zip_code || '',
        city: app.borrower_info.city || '',
        phone: app.borrower_info.phone?.replace('+354', '') || '',
        email: app.borrower_info.email || '',
        vehicleType: app.vehicle_type || '',
        registrationNumber: app.registration_number || '',
        firstRegistrationDate: toISODate(app.first_registration_date) || '',
        purchasePrice: app.purchase_price?.toString() || '',
        deposit: app.deposit?.toString() || '0',
        lantokugjald: app.lantokugjald?.toString() || '',
        greidslus: app.greidslus?.toString() || '250',
        months: app.requested_term_months?.toString() || '',
        vextir: app.interest_rate?.toString() || settings?.vextir?.toString() || ''
      });
      setShowKaupleiguForm(true);
    }
    
    toast.info("Breyting umsóknar opnuð");
  };

  const handleDeleteApplication = async (app) => {
    try {
      await deleteApplication(app.id, app.borrower_info.full_name);
      setSelectedApp(null);
    } catch (error) {
      // Error is already handled in deleteApplication
      console.error("Error in handleDeleteApplication:", error);
    }
  };

  const handleVikjaClick = async (app) => {
    try {
      // First, try to fetch existing customer data from Payday.is to get complete address
      let addressData = {
        address: app.borrower_info.address,
        zipCode: app.borrower_info.zip_code,
        city: app.borrower_info.city
      };
      
      try {
        const fetchResponse = await axios.get(`${API}/payday/fetch-customer/${app.borrower_info.ssn}`);
        if (fetchResponse.data.success && fetchResponse.data.customer) {
          // Use Payday's address data if available
          const paydayCustomer = fetchResponse.data.customer;
          addressData = {
            address: paydayCustomer.address || app.borrower_info.address,
            zipCode: paydayCustomer.zipCode || app.borrower_info.zip_code,
            city: paydayCustomer.city || app.borrower_info.city
          };
          console.log('Found existing Payday customer, using their address data');
        }
      } catch (fetchError) {
        console.log('Customer not found in Payday, will create new with available data');
      }
      
      // Create customer in Payday.is for both regular and legacy loans
      const paydayResponse = await axios.post(`${API}/payday/create-customer`, {
        kennitala: app.borrower_info.ssn,
        email: app.borrower_info.email,
        phone: app.borrower_info.phone,
        name: app.borrower_info.full_name,
        address: addressData.address,
        zipCode: addressData.zipCode,
        city: addressData.city
      });

      if (paydayResponse.data.success) {
        const customerId = paydayResponse.data.customer_id;
        toast.success(`Viðskiptavinur stofnaður í Payday.is: ${customerId || ''}`);
        
        // For legacy loans, set status to 'disbursed' (already active)
        // For regular loans, set status to 'approved' (needs disbursement)
        const newStatus = app.is_legacy ? 'disbursed' : 'approved';
        const notes = app.is_legacy 
          ? 'Eldra lán virkjað og viðskiptavinur stofnaður í Payday.is'
          : 'Lán samþykkt og viðskiptavinur stofnaður í Payday.is';
        
        // Update application status AND store payday_customer_id
        await axios.patch(`${API}/loans/${app.id}`, {
          status: newStatus,
          interest_rate: app.is_legacy ? app.interest_rate : 5.5, // Keep legacy rate for legacy loans
          notes: notes,
          payday_customer_id: customerId
        });
        
        // Refresh applications
        fetchApplications();
        setSelectedApp(null);
        
        const successMessage = app.is_legacy ? 'Eldra lán virkjað' : 'Lán samþykkt';
        toast.success(successMessage);
      } else {
        toast.error(`Villa við að stofna viðskiptavin í Payday.is: ${paydayResponse.data.message}`);
      }
    } catch (error) {
      console.error("Error in handleVikjaClick:", error);
      toast.error("Ekki tókst að stofna viðskiptavin í Payday.is");
    }
  };

  const handleVirkjaSkuldbreyting = async (app) => {
    try {
      // Step 1: Remove isSkuldbreyting flag and set status to approved
      await axios.patch(`${API}/loans/${app.id}`, {
        status: 'approved',
        isSkuldbreyting: false
      });
      
      // Step 2: Pay off old loan if oldLoanId exists
      if (app.oldLoanId) {
        try {
          // Get old loan details
          const oldLoanResponse = await axios.get(`${API}/loans`);
          const oldLoan = oldLoanResponse.data.find(l => l.id === app.oldLoanId);
          
          if (oldLoan) {
            const payoffAmount = oldLoan.remaining_balance || oldLoan.loan_amount;
            
            // Create payment for full payoff
            await axios.post(`${API}/loans/${app.oldLoanId}/payment`, {
              amount: payoffAmount,
              payment_date: new Date().toISOString().split('T')[0]
            });
            
            // Update old loan status to closed
            await axios.patch(`${API}/loans/${app.oldLoanId}`, {
              status: 'closed'
            });
            
            toast.success(`Skuldbreyting virkjuð! Eldra lán ${loanNumberMap[app.oldLoanId] ? fmtLoanNum(loanNumberMap[app.oldLoanId]) : ''} greitt upp og lokað.`);
          } else {
            toast.warning('Nýtt lán virkjað en eldra lán fannst ekki');
          }
        } catch (error) {
          console.error("Error paying off old loan:", error);
          toast.error("Nýtt lán virkjað en villa kom upp við að greiða upp eldra lán");
        }
      } else {
        toast.success('Lán virkjað');
      }
      
      // Refresh applications and close dialog
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      console.error("Error activating Skuldbreyting:", error);
      toast.error("Ekki tókst að virkja skuldbreytingu");
    }
  };

  const lookupPerson = async (kennitala, formType) => {
    if (!kennitala || kennitala.length !== 10) return;
    
    setLookupLoading(true);
    try {
      // First, try to get from cache
      try {
        const cacheResponse = await axios.get(`${API}/kennitala-cache/${kennitala}`);
        
        if (cacheResponse.data) {
          const cachedData = cacheResponse.data;
          toast.success("Upplýsingar sóttar úr gagnagrunni");
          
          // Update the appropriate form based on formType
          const formData = {
            name: cachedData.name || '',
            address: cachedData.address || '',
            postalCode: cachedData.postal_code || '',
            city: cachedData.city || '',
            phone: cachedData.phone ? cachedData.phone.replace('+354', '') : '',
            email: cachedData.email || ''
          };
          
          if (formType === 'skuldabref') {
            setSkuldabrefFormData(prev => ({...prev, ...formData}));
          } else if (formType === 'vedskuldabref') {
            setVedskuldabrefFormData(prev => ({...prev, ...formData}));
          } else if (formType === 'kaupleigusamning') {
            setKaupleiguFormData(prev => ({...prev, ...formData}));
          }
          
          setLookupLoading(false);
          return;
        }
      } catch (cacheError) {
        // Cache miss or error - continue to API lookup
        console.log("Cache miss, trying API lookup");
      }
      
      // If not in cache, try Creditinfo API
      const response = await axios.post(`${API}/creditinfo/lookup-person`, {
        kennitala: kennitala
      });
      
      if (response.data) {
        const person = response.data;
        toast.success("Upplýsingar sóttar úr Þjóðskrá");
        
        // Creditinfo now returns separate fields: name, address, postal_code, city
        let streetAddress = person.address || '';
        let postalCode = person.postal_code || '';
        let city = person.city || '';
        
        // If postal_code or city not provided by API, try parsing from address string
        if (!postalCode || !city) {
          // Parse address to extract street, postal code, and city
          // Icelandic address format is typically: "Street name Number, PostalCode City"
          // Example: "Laugavegur 1, 101 Reykjavík"
          
          if (person.address) {
            // Try to extract postal code (3 digits) and city
            // Pattern: finds 3 digits optionally followed by city name
            const addressMatch = person.address.match(/^(.+?),?\s*(\d{3})\s*(.+)$/);
            
            if (addressMatch) {
              streetAddress = addressMatch[1].trim(); // Street address before postal code
              postalCode = postalCode || addressMatch[2].trim();     // 3-digit postal code
              city = city || addressMatch[3].trim();           // City name after postal code
            } else {
              // If no match, try alternative pattern (comma separated)
              const parts = person.address.split(',').map(p => p.trim());
              if (parts.length >= 2) {
                streetAddress = parts[0];
                // Check if second part has postal code
                const postalMatch = parts[1].match(/(\d{3})\s*(.+)/);
                if (postalMatch) {
                  postalCode = postalCode || postalMatch[1];
                  city = city || postalMatch[2];
                } else {
                  city = city || parts[1];
                }
              }
            }
          }
        }
        
        // Update the appropriate form based on formType
        if (formType === 'skuldabref') {
          setSkuldabrefFormData(prev => ({
            ...prev,
            name: person.name || prev.name,
            address: streetAddress || prev.address,
            postalCode: postalCode || prev.postalCode,
            city: city || prev.city
          }));
        } else if (formType === 'vedskuldabref') {
          setVedskuldabrefFormData(prev => ({
            ...prev,
            name: person.name || prev.name,
            address: streetAddress || prev.address,
            postalCode: postalCode || prev.postalCode,
            city: city || prev.city
          }));
        } else if (formType === 'kaupleigusamning') {
          setKaupleiguFormData(prev => ({
            ...prev,
            name: person.name || prev.name,
            address: streetAddress || prev.address,
            postalCode: postalCode || prev.postalCode,
            city: city || prev.city
          }));
        }
      }
    } catch (error) {
      console.error("Error looking up person:", error);
      toast.error(error.response?.data?.detail || "Villa kom upp við uppflettingu");
    } finally {
      setLookupLoading(false);
    }
  };

  const saveKennitalCache = async (kennitala, name, address, postalCode, city, phone = '', email = '') => {
    if (!kennitala || kennitala.length !== 10) return;
    
    try {
      await axios.post(`${API}/kennitala-cache`, {
        kennitala,
        name,
        address,
        postal_code: postalCode,
        city,
        phone: phone ? `+354${phone.replace('+354', '')}` : '',
        email
      });
      console.log(`Kennitala ${kennitala} cached successfully`);
    } catch (error) {
      console.error("Error caching kennitala:", error);
      // Don't show error to user - this is a background operation
    }
  };

  const fetchPaydayCustomer = async (kennitala, formType) => {
    if (!kennitala || kennitala.length !== 10) return;
    
    try {
      const response = await axios.get(`${API}/payday/fetch-customer/${kennitala}`);
      
      if (response.data.success && response.data.customer) {
        const customer = response.data.customer;
        toast.success("Viðskiptavinur fundinn í Payday.is");
        
        // Update the appropriate form based on formType
        if (formType === 'skuldabref') {
          setSkuldabrefFormData(prev => ({
            ...prev,
            name: customer.name || prev.name,
            address: customer.address || prev.address,
            city: customer.city || prev.city,
            postalCode: customer.zipCode || prev.postalCode,
            email: customer.email || prev.email,
            phone: customer.phone?.replace('+354', '') || prev.phone
          }));
        } else if (formType === 'vedskuldabref') {
          setVedskuldabrefFormData(prev => ({
            ...prev,
            name: customer.name || prev.name,
            address: customer.address || prev.address,
            city: customer.city || prev.city,
            postalCode: customer.zipCode || prev.postalCode,
            email: customer.email || prev.email,
            phone: customer.phone?.replace('+354', '') || prev.phone
          }));
        } else if (formType === 'kaupleigusamning') {
          setKaupleiguFormData(prev => ({
            ...prev,
            name: customer.name || prev.name,
            address: customer.address || prev.address,
            city: customer.city || prev.city,
            postalCode: customer.zipCode || prev.postalCode
          }));
        }
      } else {
        // Customer not found - this is OK, user can fill in manually
        console.log("Customer not found in Payday.is");
      }
    } catch (error) {
      console.error("Error fetching customer from Payday.is:", error);
      // Don't show error toast - just let user fill in manually
    }
  };

  if (loading) {
    return <div className="p-8">Hleð umsóknum...</div>;
  }

  // Filter applications by kennitala and loan number search
  const filteredApplications = applications.filter(app => {
    const kennitalMatch = searchKennitala.trim()
      ? app.borrower_info?.ssn?.includes(searchKennitala.trim())
      : true;
    
    return kennitalMatch;
  });
  
  // Sort applications by priority: Skuldbreyting/Senda allt → Í bið → Virk → Lokið
  const sortedApplications = [...filteredApplications].sort((a, b) => {
    // Define priority order
    const getPriority = (app) => {
      if (app.isSkuldbreyting) return 1; // Skuldbreyting (highest priority)
      // Add veðfluttning check when available
      // if (app.isVedfluttning) return 1;
      if (app.status === 'pending') return 2; // Í bið
      if (app.status === 'approved' || app.status === 'disbursed') return 3; // Virk (active)
      if (app.status === 'closed') return 4; // Lokið (lowest priority)
      return 5; // Others (rejected, under_review, etc.)
    };
    
    const priorityA = getPriority(a);
    const priorityB = getPriority(b);
    
    // Sort by priority first
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // Within same priority, sort by creation date (newest first)
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Create loan number mapping
  // Loan numbers: only use permanently stored numbers from DB (sequential, never repeated)
  const loanNumberMap = {};
  sortedApplications.forEach(app => {
    if (app.loan_number) loanNumberMap[app.id] = app.loan_number;
  });

  // Format loan number: always 4 digits with "Láns nr." prefix
  const fmtLoanNum = (num) => num != null ? `Láns nr. ${String(num).padStart(4, '0')}` : '';

  return (
    <div className="p-8" data-testid="applications-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Lánaumsóknir</h1>
          <div className="flex gap-2">
            <Button 
              onClick={async () => {
                try {
                  toast.loading("Samstilli greiðslur...", { id: 'sync-all' });
                  
                  const response = await axios.post(`${API}/payday/sync-payment-statuses`);
                  
                  if (response.data.success) {
                    toast.success(
                      `✓ ${response.data.payments_updated} greiðslur uppfærðar`, 
                      { id: 'sync-all', duration: 5000 }
                    );
                    // Refresh applications
                    fetchApplications();
                  } else {
                    toast.error("Villa við samstillingu", { id: 'sync-all' });
                  }
                } catch (error) {
                  console.error("Error syncing payments:", error);
                  toast.error("Villa við samstillingu", { id: 'sync-all' });
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 h-6 text-[10px] px-2 flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" />
              Greiðslur
            </Button>
            {shouldShowSendaAllt() && (
              <Button 
                onClick={handleSendaAllt}
                className="bg-blue-600 hover:bg-blue-700 h-6 text-[10px] px-2"
                data-testid="senda-allt-btn"
              >
                Senda allt
              </Button>
            )}
            <Button 
              onClick={() => setShowSkuldbreytingDialog(true)}
              className="bg-blue-600 hover:bg-blue-700 h-6 text-[10px] px-2"
              data-testid="skuldbreyting-btn"
            >
              Skuldbreyting
            </Button>
            <Button 
              onClick={() => setShowNewForm(true)}
              className="bg-blue-600 hover:bg-blue-700 h-6 text-[10px] px-2"
              data-testid="new-application-btn"
            >
              Ný Umsókn
            </Button>
          </div>
        </div>

        <div className="mb-4 flex gap-4 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Allar umsóknir</SelectItem>
              <SelectItem value="pending">Í bið</SelectItem>
              <SelectItem value="virk">Virk</SelectItem>
              <SelectItem value="closed">Lokið</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Leita eftir kennitölu..."
              value={searchKennitala}
              onChange={(e) => setSearchKennitala(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Summary counts */}
        {(() => {
          const virkCount = applications.filter(a => a.status === 'disbursed' || a.status === 'approved').length;
          const ibidCount = applications.filter(a => a.status === 'pending').length;
          const lokidCount = applications.filter(a => a.status === 'closed').length;
          return (
            <div className="flex items-center gap-4 mb-3 text-[10px] text-gray-500">
              <span className="text-green-600">Virk <span className="font-semibold">{virkCount}</span></span>
              <span className="text-yellow-600">Í bið <span className="font-semibold">{ibidCount}</span></span>
              <span className="text-gray-400">Lokið <span className="font-semibold">{lokidCount}</span></span>
            </div>
          );
        })()}

        <div className="flex flex-col gap-1.5">
          {sortedApplications.map((app) => {
            const isVirk = app.status === 'disbursed' || app.status === 'approved';
            const maxAhk = 0.35 + (settings.styrivextir || 7.25) / 100;
            const { ahk } = app.loan_amount > 0 && app.requested_term_months > 0 && app.interest_rate > 0
              ? calculateLoanAhk(app.loan_amount, app.lantokugjald || 0, app.requested_term_months, app.interest_rate, app.greidslus || 250)
              : { ahk: 0 };
            const ahkOver = ahk > 0 && ahk > maxAhk;
            const ahkOk = ahk > 0 && ahk <= maxAhk;
            const cardClass = isVirk
              ? (ahkOver ? 'border-red-100 shadow-[0_0_0_1px_rgba(248,113,113,0.2)]' : ahkOk ? 'border-green-100 shadow-[0_0_0_1px_rgba(134,239,172,0.2)]' : 'border-gray-200')
              : 'border-gray-200';
            const ahkTitle = ahk > 0 ? `ÁHK: ${(ahk * 100).toFixed(2)}% | Hámark: ${(maxAhk * 100).toFixed(2)}%` : '';

            const createdDate = new Date(app.created_at);
            const now = new Date();
            const monthsPassed = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24 * 30));
            const monthsLeft = Math.max(0, app.requested_term_months - monthsPassed);

            const loanTypeLabel = app.loan_type === 'vedskuldabref' ? 'Veðskuldabréf' :
              app.loan_type === 'kaupleigusamning' ? 'Kaupleigusamningur' :
              app.loan_type === 'eldra_lan' ? 'Eldra lán' : 'Skuldabréf';

            return (
              <div
                key={app.id}
                data-testid={`application-row-${app.id}`}
                className={`rounded-lg border bg-white px-3 py-2 flex items-center gap-3 cursor-pointer hover:border-blue-200 hover:bg-blue-50/30 ${cardClass}`}
                title={ahkTitle}
                onClick={() => navigate(`/greidslur/${app.id}`)}
              >
                {/* Status badge - left side */}
                <div className="shrink-0 w-14">
                  {getStatusBadge(app.status, app)}
                </div>

                {/* Name + meta */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-xs truncate">{app.borrower_info.full_name}</span>
                    <span className="text-[9px] text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 shrink-0">{loanTypeLabel}</span>
                    {app.is_legacy && <span className="text-[9px] text-yellow-600 font-semibold shrink-0">Eldra lán</span>}
                    {app.status !== 'closed' && app.scheduled_payments_status && Object.values(app.scheduled_payments_status).some(s => s === 'invoice_sent') && (
                      <span className="text-[9px] text-blue-600 font-semibold shrink-0">Reikningur sendur</span>
                    )}
                  </div>
                  <div className="text-[10px] text-gray-400 truncate">{app.borrower_info.email}</div>
                </div>

                {/* loan# + months */}
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-[10px] font-mono text-gray-500 w-[7.5rem] text-left tabular-nums">
                    {fmtLoanNum(loanNumberMap[app.id])}
                  </span>
                  <span className="text-[10px] text-gray-400 w-10 text-right tabular-nums">{monthsLeft} mán.</span>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-0.5 shrink-0 border-l pl-2" onClick={(e) => e.stopPropagation()}>
                  {app.status === 'pending' && (
                    <Button variant="ghost" onClick={() => handleEditApplication(app)} data-testid={`edit-application-${app.id}`} className="p-1 h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Breytta">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {(!app.is_legacy || (app.is_legacy && app.status === 'pending')) && app.status !== 'disbursed' && app.status !== 'approved' && (
                    <Button variant="ghost" onClick={() => deleteApplication(app.id, app.borrower_info.full_name)} data-testid={`delete-application-${app.id}`} className="p-1 h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50" title="Eyða">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {(app.status === 'approved' || app.status === 'disbursed') && (
                    <Button variant="ghost" onClick={() => { setAfsolWizardLoanData(app); setShowAfsolWizard(true); }} data-testid={`create-afsal-${app.id}`} className="p-1 h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Útbúa afsal út frá forsendu lánsins">
                      <FilePlus2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {app.is_legacy ? (
                    <Button variant="ghost" onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/loans/${app.id}/document`);
                        const pdfData = response.data;
                        if (!pdfData.content) { toast.error("PDF skjal fannst ekki"); return; }
                        const byteCharacters = atob(pdfData.content);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
                        window.open(URL.createObjectURL(new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' })), '_blank');
                      } catch (error) {
                        toast.error(error.response?.status === 404 ? "PDF skjal fannst ekki." : "Ekki tókst að opna PDF skjal.");
                      }
                    }} data-testid={`view-legacy-document-${app.id}`} className="p-1 h-6 w-6 text-purple-600 hover:text-purple-700 hover:bg-purple-50" title="Skoða upprunalegt skjal">
                      <FileText className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <>
                      <Button variant="ghost" onClick={() => { setIsElectronicSignature(false); setPdfApp(app); setPdfType(app.loan_type === 'vedskuldabref' ? 'vedskuldabref' : app.loan_type === 'kaupleigusamning' ? 'kaupleigusamningur' : 'skuldabref'); }} data-testid={`pdf-application-${app.id}`} className="p-1 h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-50" title="Prenta">
                        <FileText className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" onClick={() => { setDokobitApp(app); setDokobitSigners([{ name: app.borrower_info?.full_name?.split(' ')[0] || '', surname: app.borrower_info?.full_name?.split(' ').slice(1).join(' ') || '', code: app.borrower_info?.ssn || '', country_code: 'is', signing_purpose: 'signature' }]); setShowDokobitDialog(true); setSigningUrls(null); setSigningStatus(null); }} data-testid={`dokobit-sign-${app.id}`} className="p-1 h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-50" title="Senda í undirritun með Dokobit">
                        <div className="relative"><FileText className="h-3.5 w-3.5" /><Zap className="h-2.5 w-2.5 absolute -top-1 -right-1" /></div>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Application Review Dialog */}
        <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            {selectedApp && (
              <>
                <DialogHeader>
                  <DialogTitle>Skoða lánaumsókn</DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold mb-2">Upplýsingar lántaka</h3>
                      <div className="space-y-1 text-sm">
                        <p><strong>Kennitala:</strong> {selectedApp.borrower_info.ssn || 'N/A'}</p>
                        <p><strong>Nafn:</strong> {selectedApp.borrower_info.full_name}</p>
                        <div className="flex items-center gap-1">
                          <strong className="text-sm">Netfang:</strong>
                          <Input
                            type="email"
                            id="view-app-email-input"
                            defaultValue={selectedApp.borrower_info.email || ''}
                            className="h-6 text-xs flex-1"
                            data-testid="view-app-email"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <strong className="text-sm">Sími:</strong>
                          <Input
                            type="tel"
                            id="view-app-phone-input"
                            defaultValue={selectedApp.borrower_info.phone || ''}
                            className="h-6 text-xs flex-1"
                            data-testid="view-app-phone"
                          />
                        </div>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 h-6 text-xs px-3 mt-1"
                          data-testid="save-contact-btn"
                          onClick={async () => {
                            const newEmail = document.getElementById('view-app-email-input')?.value || '';
                            const newPhone = document.getElementById('view-app-phone-input')?.value || '';
                            const updates = {};
                            if (newEmail !== (selectedApp.borrower_info.email || '')) updates.email = newEmail;
                            if (newPhone !== (selectedApp.borrower_info.phone || '')) updates.phone = newPhone;
                            if (Object.keys(updates).length === 0) {
                              toast.info("Engar breytingar");
                              return;
                            }
                            try {
                              await axios.patch(`${API}/loans/${selectedApp.id}`, {
                                borrower_info: updates
                              });
                              if (updates.email) selectedApp.borrower_info.email = updates.email;
                              if (updates.phone) selectedApp.borrower_info.phone = updates.phone;
                              toast.success("Upplýsingar vistaðar", { duration: 4000 });
                              fetchApplications();
                            } catch (err) {
                              toast.error("Villa við að vista");
                            }
                          }}
                        >
                          <Check className="h-3 w-3 mr-1" />Vista
                        </Button>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Lánaupplýsingar</h3>
                      <div className="space-y-1 text-sm">
                        <p><strong>Upphæð:</strong> {formatIcelandicNumber(Math.round(selectedApp.loan_amount))} kr.</p>
                        <p><strong>Tegund:</strong> {getLoanTypeLabel(selectedApp.loan_type)}</p>
                        <p><strong>Lánstími:</strong> {selectedApp.requested_term_months} mánuðir</p>
                        <p><strong>Mánuðir eftir:</strong> {(() => {
                          const createdDate = new Date(selectedApp.created_at);
                          const now = new Date();
                          const monthsPassed = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24 * 30));
                          const monthsLeft = Math.max(0, selectedApp.requested_term_months - monthsPassed);
                          return `${monthsLeft} mánuðir`;
                        })()}</p>
                        <p><strong>Eftirstöðvar:</strong> {formatIcelandicNumber(Math.round(selectedApp.remaining_balance || selectedApp.loan_amount))} kr.</p>
                      </div>
                    </div>
                  </div>
                  
                  {selectedApp.isSkuldbreyting && selectedApp.status === 'pending' && (
                    <div className="flex space-x-4">
                      <Button
                        onClick={() => handleVirkjaSkuldbreyting(selectedApp)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="virkja-skuldbreyting-btn"
                      >
                        Virkja
                      </Button>
                    </div>
                  )}
                  
                  {!selectedApp.isSkuldbreyting && selectedApp.status === 'pending' && (
                    <div className="flex space-x-4">
                      <Button
                        onClick={() => handleVikjaClick(selectedApp)}
                        className="bg-green-600 hover:bg-green-700 h-7 text-xs px-2"
                        data-testid="approve-btn"
                      >
                        Vikja
                      </Button>
                      <Button
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-50"
                        onClick={() => handleEditApplication(selectedApp)}
                        data-testid="edit-btn"
                      >
                        breytta
                      </Button>
                    </div>
                  )}
                  
                  {!selectedApp.is_legacy && selectedApp.status === 'approved' && (
                    <div className="flex space-x-4">
                      <Button
                        variant="destructive"
                        onClick={() => handleDeleteApplication(selectedApp)}
                        data-testid="delete-approved-btn"
                      >
                        Eyða
                      </Button>
                    </div>
                  )}
                  
                  {/* Loan Type Conversion Button - Kaupleigusamningur ↔ Veðskuldabréf */}
                  {(selectedApp.loan_type === 'kaupleigusamning' || selectedApp.loan_type === 'vedskuldabref') && (
                    <div className="flex space-x-4">
                      <Button
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-50"
                        onClick={async () => {
                          const currentType = selectedApp.loan_type;
                          const newType = currentType === 'kaupleigusamning' ? 'vedskuldabref' : 'kaupleigusamning';
                          const newTypeLabel = currentType === 'kaupleigusamning' ? 'Veðskuldabréf' : 'Kaupleigusamningur';
                          
                          const confirmed = window.confirm(
                            `Ertu viss um að þú viljir breyta þessari umsókn í ${newTypeLabel}?\n\n` +
                            `Núverandi tegund: ${getLoanTypeLabel(currentType)}\n` +
                            `Ný tegund: ${newTypeLabel}`
                          );
                          
                          if (!confirmed) return;
                          
                          try {
                            await axios.patch(`${API}/loans/${selectedApp.id}`, {
                              loan_type: newType
                            });
                            
                            toast.success(`Lánategund breytt í ${newTypeLabel}!`);
                            
                            // Refresh applications
                            fetchApplications();
                            setSelectedApp(null);
                          } catch (error) {
                            console.error("Error converting loan type:", error);
                            toast.error("Ekki tókst að breyta lánategund");
                          }
                        }}
                        data-testid="convert-loan-type-btn"
                      >
                        {selectedApp.loan_type === 'kaupleigusamning' 
                          ? 'Breyta í Veðskuldabréf' 
                          : 'Breyta í Kaupleigusamningur'}
                      </Button>
                    </div>
                  )}
                  
                  {selectedApp.is_legacy && selectedApp.status === 'pending' && (
                    <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded space-y-4">
                      <h3 className="font-semibold text-purple-900">Eldra lán - Skjöl og stillingar</h3>
                      
                      {/* PDF Upload */}
                      <div>
                        <Label htmlFor="upload-legacy-pdf" className="text-sm font-medium">
                          Hlaða upp/Skipta út PDF skjali
                        </Label>
                        <Input
                          id="upload-legacy-pdf"
                          type="file"
                          accept=".pdf"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            try {
                              toast.info("Hleð upp PDF...");
                              
                              const formData = new FormData();
                              formData.append('pdf_file', file);
                              
                              await axios.post(
                                `${API}/loans/${selectedApp.id}/upload-pdf`,
                                formData,
                                { headers: { 'Content-Type': 'multipart/form-data' } }
                              );
                              
                              toast.success("PDF skjal hlaðið upp!");
                              fetchApplications();
                            } catch (error) {
                              console.error("Error uploading PDF:", error);
                              toast.error("Ekki tókst að hlaða upp PDF");
                            }
                          }}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Veldu PDF skjal til að hlaða upp eða skipta út fyrra skjali
                        </p>
                      </div>
                      
                      {/* Edit Term */}
                      <div>
                        <Label htmlFor="edit-term" className="text-sm font-medium">
                          Breyta lánstíma (Mánuðir)
                        </Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            id="edit-term"
                            type="number"
                            defaultValue={selectedApp.requested_term_months}
                            className="w-32"
                            min="1"
                            max="120"
                          />
                          <Button
                            size="sm" className="h-7 text-xs px-2"
                            onClick={async () => {
                              const newTerm = document.getElementById('edit-term').value;
                              if (!newTerm || newTerm < 1) {
                                toast.error("Ógildur lánstími");
                                return;
                              }
                              
                              try {
                                await axios.patch(`${API}/loans/${selectedApp.id}`, {
                                  requested_term_months: parseInt(newTerm)
                                });
                                
                                toast.success("Lánstími uppfærður!");
                                setSelectedApp(null);
                                fetchApplications();
                              } catch (error) {
                                console.error("Error updating term:", error);
                                toast.error("Ekki tókst að uppfæra lánstíma");
                              }
                            }}
                          >
                            Vista
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Breyta lánstíma fyrir eldra lán í bið
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {selectedApp.is_legacy && selectedApp.status !== 'pending' && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-sm text-yellow-800">
                        <strong>Athugasemd:</strong> Þetta er eldra lán. Þú getur hlaðið upp nýju PDF skjali eða breytt lánstíma hér að ofan.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* New Application Form */}
        <Dialog open={showNewForm} onOpenChange={(open) => {
          setShowNewForm(open);
          if (!open) {
            setSelectedLoanType(null);
          }
        }}>
          <DialogContent className="max-w-2xl">
            {!selectedLoanType ? (
              <>
                <DialogHeader>
                  <DialogTitle>Veldu lánategund</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  <p className="text-gray-600 text-center mb-6">
                    Veldu þá lánategund sem þú vilt sækja um:
                  </p>
                  
                  <div className="grid gap-4">
                    <Card 
                      className="cursor-pointer hover:bg-blue-50 border-2 hover:border-blue-200 transition-all"
                      onClick={() => {
                        setShowNewForm(false);
                        setSelectedLoanType(null);
                        setShowSkuldabrefForm(true);
                        // Use pending new rate for new loans, otherwise current vextir
                        const prc = settings.pending_rate_change;
                        const rate = (prc && prc.status === 'pending') ? prc.new_rate.toFixed(2) : (settings.vextir?.toFixed(2) || '17.7');
                        setSkuldabrefFormData(prev => ({
                          ...prev,
                          vextir: rate
                        }));
                      }}
                      data-testid="loan-type-skuldabref"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="bg-blue-100 p-3 rounded-full">
                            <FileText className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-base">Skuldabréf</h3>
                            <p className="text-gray-600 text-sm">Almennt lán með skuldabréfi sem tryggingu</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="cursor-pointer hover:bg-green-50 border-2 hover:border-green-200 transition-all"
                      onClick={() => {
                        setShowNewForm(false);
                        setSelectedLoanType(null);
                        setShowVedskuldabrefForm(true);
                        const prc2 = settings.pending_rate_change;
                        const rate2 = (prc2 && prc2.status === 'pending') ? prc2.new_rate.toFixed(2) : (settings.vextir?.toFixed(2) || '17.7');
                        setVedskuldabrefFormData(prev => ({
                          ...prev,
                          vextir: rate2
                        }));
                      }}
                      data-testid="loan-type-vedskuldabref"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="bg-green-100 p-3 rounded-full">
                              <DollarSign className="h-6 w-6 text-green-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-base">Veðskuldabréf</h3>
                              <p className="text-gray-600 text-sm">Lán með veði í fasteign</p>
                            </div>
                          </div>
                          <div className="text-green-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="cursor-pointer hover:bg-purple-50 border-2 hover:border-purple-200 transition-all"
                      onClick={() => {
                        setShowNewForm(false);
                        setSelectedLoanType(null);
                        setShowKaupleiguForm(true);
                        const prc3 = settings.pending_rate_change;
                        const rate3 = (prc3 && prc3.status === 'pending') ? prc3.new_rate.toFixed(2) : (settings.vextir?.toFixed(2) || '17.7');
                        setKaupleiguFormData(prev => ({
                          ...prev,
                          vextir: rate3
                        }));
                      }}
                      data-testid="loan-type-kaupleigusamning"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="bg-purple-100 p-3 rounded-full">
                              <TrendingUp className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-base">Kaupleigusamningur</h3>
                              <p className="text-gray-600 text-sm">Fjármögnun með kaupleigusamningi</p>
                            </div>
                          </div>
                          <div className="text-purple-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="text-center pt-4">
                    <Button 
                      variant="outline"
                      onClick={() => setShowNewForm(false)}
                      className="w-32"
                    >
                      Hætta við
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {selectedLoanType === "vedskuldabref" ? "Veðskuldabréf" : "Kaupleigusamningur"} - Veldu tegund
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <Button 
                      variant="ghost" 
                      size="sm" className="h-7 text-xs px-2"
                      onClick={() => setSelectedLoanType(null)}
                      className="text-gray-600 hover:text-gray-800"
                    >
                      ← Til baka
                    </Button>
                  </div>
                  
                  <p className="text-gray-600 text-center mb-6">
                    Veldu hvort um er að ræða einstakling eða lögaðila:
                  </p>
                  
                  <div className="grid gap-4">
                    <Card 
                      className={`cursor-pointer hover:bg-${selectedLoanType === "vedskuldabref" ? "green" : "purple"}-50 border-2 hover:border-${selectedLoanType === "vedskuldabref" ? "green" : "purple"}-200 transition-all`}
                      onClick={() => {
                        setShowNewForm(false);
                        setSelectedLoanType(null);
                        if (selectedLoanType === "kaupleigusamning") {
                          // Open Kaupleigusamningur dialog form
                          setShowKaupleiguForm(true);
                        } else if (selectedLoanType === "vedskuldabref") {
                          // Open Veðskuldabréf dialog form
                          setShowVedskuldabrefForm(true);
                        }
                      }}
                      data-testid="entity-type-individual"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className={`bg-${selectedLoanType === "vedskuldabref" ? "green" : "purple"}-100 p-3 rounded-full`}>
                            <Users className={`h-6 w-6 text-${selectedLoanType === "vedskuldabref" ? "green" : "purple"}-600`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-base">Einstaklingur</h3>
                            <p className="text-gray-600 text-sm">Einstaklingur sem sækir um lán</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className={`cursor-pointer hover:bg-${selectedLoanType === "vedskuldabref" ? "green" : "purple"}-50 border-2 hover:border-${selectedLoanType === "vedskuldabref" ? "green" : "purple"}-200 transition-all`}
                      onClick={() => {
                        setShowNewForm(false);
                        setSelectedLoanType(null);
                        toast.success(`${selectedLoanType === "vedskuldabref" ? "Veðskuldabréf" : "Kaupleigusamningur"} fyrir lögaðila valinn - form yrði opnað hér`);
                      }}
                      data-testid="entity-type-company"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className={`bg-${selectedLoanType === "vedskuldabref" ? "green" : "purple"}-100 p-3 rounded-full`}>
                            <CheckCircle className={`h-6 w-6 text-${selectedLoanType === "vedskuldabref" ? "green" : "purple"}-600`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-base">Lögaðili</h3>
                            <p className="text-gray-600 text-sm">Fyrirtæki eða önnur lögaðili</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="text-center pt-4">
                    <Button 
                      variant="outline"
                      onClick={() => setSelectedLoanType(null)}
                      className="w-32 mr-2"
                    >
                      Til baka
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowNewForm(false);
                        setSelectedLoanType(null);
                      }}
                      className="w-32"
                    >
                      Hætta við
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* PDF Preview Dialog */}
        <PdfPreviewDialog
          pdfApp={pdfApp}
          setPdfApp={setPdfApp}
          pdfType={pdfType}
          setPdfType={setPdfType}
          isElectronicSignature={isElectronicSignature}
          setIsElectronicSignature={setIsElectronicSignature}
          settings={settings}
          loanNumberMap={loanNumberMap}
          fmtLoanNum={fmtLoanNum}
          applications={applications}
          fetchApplications={fetchApplications}
          setShowDokobitDialog={setShowDokobitDialog}
          setDokobitApp={setDokobitApp}
          setDokobitSigners={setDokobitSigners}
          handleEditApplication={handleEditApplication}
          setShowSkuldabrefForm={setShowSkuldabrefForm}
          setShowVedskuldabrefForm={setShowVedskuldabrefForm}
          setShowKaupleiguForm={setShowKaupleiguForm}
          skuldabrefFormData={skuldabrefFormData}
          vedskuldabrefFormData={vedskuldabrefFormData}
          kaupleiguFormData={kaupleiguFormData}
        />
        {/* Dokobit Dialog */}
        <DokobitDialog
          open={showDokobitDialog}
          onOpenChange={setShowDokobitDialog}
          dokobitApp={dokobitApp}
          dokobitSigners={dokobitSigners}
          setDokobitSigners={setDokobitSigners}
          dokobitLoading={dokobitLoading}
          setDokobitLoading={setDokobitLoading}
          signingUrls={signingUrls}
          setSigningUrls={setSigningUrls}
          fetchApplications={fetchApplications}
          setIsElectronicSignature={setIsElectronicSignature}
          setPdfApp={setPdfApp}
          setPdfType={setPdfType}
        />
        {/* Skuldabréf Form */}
        <SkuldabrefFormDialog
          open={showSkuldabrefForm}
          onOpenChange={setShowSkuldabrefForm}
          formData={skuldabrefFormData}
          setFormData={setSkuldabrefFormData}
          settings={settings}
          lookupLoading={lookupLoading}
          lookupPerson={lookupPerson}
          vehicleLookupLoading={vehicleLookupLoading}
          setVehicleLookupLoading={setVehicleLookupLoading}
          vehicleLookupFailed={vehicleLookupFailed}
          setVehicleLookupFailed={setVehicleLookupFailed}
          showMiniSpreadsheet={showMiniSpreadsheet}
          setShowMiniSpreadsheet={setShowMiniSpreadsheet}
          editMode={editMode}
          setEditMode={setEditMode}
          editingAppId={editingAppId}
          setEditingAppId={setEditingAppId}
          skuldbreytingOldLoanId={skuldbreytingOldLoanId}
          setSkuldbreytingOldLoanId={setSkuldbreytingOldLoanId}
          skuldbreytingOldLoanIds={skuldbreytingOldLoanIds}
          loanNumberMap={loanNumberMap}
          fmtLoanNum={fmtLoanNum}
          setEmailPreviewData={setEmailPreviewData}
          setShowEmailPreview={setShowEmailPreview}
          fetchApplications={fetchApplications}
          saveKennitalCache={saveKennitalCache}
          getDefaultFirstPaymentDate={getDefaultFirstPaymentDate}
          setPdfApp={setPdfApp}
          setPdfType={setPdfType}
        />
        {/* Vedskuldabréf Form */}
        <VedskuldabrefFormDialog
          open={showVedskuldabrefForm}
          onOpenChange={setShowVedskuldabrefForm}
          formData={vedskuldabrefFormData}
          setFormData={setVedskuldabrefFormData}
          settings={settings}
          lookupLoading={lookupLoading}
          lookupPerson={lookupPerson}
          vehicleLookupLoading={vehicleLookupLoading}
          setVehicleLookupLoading={setVehicleLookupLoading}
          vehicleLookupFailed={vehicleLookupFailed}
          setVehicleLookupFailed={setVehicleLookupFailed}
          showMiniSpreadsheet={showMiniSpreadsheet}
          setShowMiniSpreadsheet={setShowMiniSpreadsheet}
          editMode={editMode}
          setEditMode={setEditMode}
          editingAppId={editingAppId}
          setEditingAppId={setEditingAppId}
          skuldbreytingOldLoanId={skuldbreytingOldLoanId}
          setSkuldbreytingOldLoanId={setSkuldbreytingOldLoanId}
          skuldbreytingOldLoanIds={skuldbreytingOldLoanIds}
          loanNumberMap={loanNumberMap}
          fmtLoanNum={fmtLoanNum}
          setEmailPreviewData={setEmailPreviewData}
          setShowEmailPreview={setShowEmailPreview}
          fetchApplications={fetchApplications}
          saveKennitalCache={saveKennitalCache}
          getDefaultFirstPaymentDate={getDefaultFirstPaymentDate}
          setPdfApp={setPdfApp}
          setPdfType={setPdfType}
        />
        {/* Kaupleigusamningur Form */}
        <KaupleiguFormDialog
          open={showKaupleiguForm}
          onOpenChange={setShowKaupleiguForm}
          formData={kaupleiguFormData}
          setFormData={setKaupleiguFormData}
          settings={settings}
          lookupLoading={lookupLoading}
          lookupPerson={lookupPerson}
          vehicleLookupLoading={vehicleLookupLoading}
          setVehicleLookupLoading={setVehicleLookupLoading}
          vehicleLookupFailed={vehicleLookupFailed}
          setVehicleLookupFailed={setVehicleLookupFailed}
          showMiniSpreadsheet={showMiniSpreadsheet}
          setShowMiniSpreadsheet={setShowMiniSpreadsheet}
          editMode={editMode}
          setEditMode={setEditMode}
          editingAppId={editingAppId}
          setEditingAppId={setEditingAppId}
          skuldbreytingOldLoanId={skuldbreytingOldLoanId}
          setSkuldbreytingOldLoanId={setSkuldbreytingOldLoanId}
          skuldbreytingOldLoanIds={skuldbreytingOldLoanIds}
          loanNumberMap={loanNumberMap}
          fmtLoanNum={fmtLoanNum}
          setEmailPreviewData={setEmailPreviewData}
          setShowEmailPreview={setShowEmailPreview}
          fetchApplications={fetchApplications}
          saveKennitalCache={saveKennitalCache}
          getDefaultFirstPaymentDate={getDefaultFirstPaymentDate}
          setPdfApp={setPdfApp}
          setPdfType={setPdfType}
        />
        {/* Skuldbreyting Dialog */}
        <SkuldbreytingDialog
          open={showSkuldbreytingDialog}
          onOpenChange={setShowSkuldbreytingDialog}
          skuldbreytingLoanNumber={skuldbreytingLoanNumber}
          setSkuldbreytingLoanNumber={setSkuldbreytingLoanNumber}
          skuldbreytingLoan={skuldbreytingLoan}
          setSkuldbreytingLoan={setSkuldbreytingLoan}
          skuldbreytingSelectedLoans={skuldbreytingSelectedLoans}
          setSkuldbreytingSelectedLoans={setSkuldbreytingSelectedLoans}
          skuldbreytingStep={skuldbreytingStep}
          setSkuldbreytingStep={setSkuldbreytingStep}
          skuldbreytingNewLoanType={skuldbreytingNewLoanType}
          setSkuldbreytingNewLoanType={setSkuldbreytingNewLoanType}
          setSkuldbreytingOldLoanId={setSkuldbreytingOldLoanId}
          setSkuldbreytingOldLoanIds={setSkuldbreytingOldLoanIds}
          setShowSkuldabrefForm={setShowSkuldabrefForm}
          setShowVedskuldabrefForm={setShowVedskuldabrefForm}
          setShowKaupleiguForm={setShowKaupleiguForm}
          setSkuldabrefFormData={setSkuldabrefFormData}
          setVedskuldabrefFormData={setVedskuldabrefFormData}
          setKaupleiguFormData={setKaupleiguFormData}
          applications={applications}
          settings={settings}
          loanNumberMap={loanNumberMap}
          fmtLoanNum={fmtLoanNum}
        />
        {/* Payment Dialog */}
        <PaymentDialog
          open={showPaymentDialog}
          onOpenChange={setShowPaymentDialog}
          paymentApp={paymentApp}
          setPaymentApp={setPaymentApp}
          paymentAmount={paymentAmount}
          setPaymentAmount={setPaymentAmount}
          fetchApplications={fetchApplications}
        />
        {/* Email Preview Dialog */}
        <EmailPreviewDialog
          show={showEmailPreview}
          onClose={() => setShowEmailPreview(false)}
          data={emailPreviewData}
          onDataChange={setEmailPreviewData}
          apiUrl={API_URL}
        />
        <NewAfsolWizard
          open={showAfsolWizard}
          onOpenChange={(v) => { setShowAfsolWizard(v); if (!v) setAfsolWizardLoanData(null); }}
          createdBy={user?.full_name || "Javier Enrique"}
          loanData={afsolWizardLoanData}
          onCreated={() => {}}
        />

      </div>
    </div>
  );
};


